import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import { Psychology, Search, Refresh } from '@mui/icons-material'
import { TextField, InputAdornment, FormControl, Select, MenuItem, IconButton /* , ToggleButtonGroup, ToggleButton */ } from '@mui/material'

// Third-party Imports
import { rankItem } from '@tanstack/match-sorter-utils'

// Component Imports
import CampaignPreparationTable from '../organisms/CampaignPreparationTable'
import CampaignProgressBar from '../../../../../campaigns/components/CampaignProgressBar'

import { getCampaignFirmsAndState, getCampaign, updateCampaignFirmSelection, approveCampaignFirmSelection, downloadCampaignFirmsExcel, abortSelectFirms } from '../../../services/B2BServices'
import TitleFilterDialog from '../molecules/TitleFilterDialog'
import { getUrlParams } from '../../../services/urlServices'
import { useToast } from '../../../../../campaigns/hooks/useToast'
import { useGlobalStoreActions } from '../../../../../store/globalStore'
import TableViewIcon from '@mui/icons-material/Download'

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)

  addMeta({
    itemRank
  })

  return itemRank.passed
}

const CampaignPreparation = ({ onCampaignUpdate, campaign, showProgressBar = false }) => {
  const { t } = useTranslation()
  const queryParams = getUrlParams()

  const { showSuccess, showError } = useToast()
  const { setCampaignStatus } = useGlobalStoreActions()

  const [relevantFilter] = useState('all')
  const [nextLoading, setNextLoading] = useState(false)
  const [campaignData, setCampaignData] = useState(null)

  const [firms, setFirms] = useState([])
  const [fetching, setFetching] = useState(true)
  const [rowSelection, setRowSelection] = useState({})

  const intervalRef = useRef(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [pageSize, setPageSize] = useState(10)

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [saveSelectedLoading, setSaveSelectedLoading] = useState(false)
  const [originalRowSelection, setOriginalRowSelection] = useState({})
  const [abortLoading, setAbortLoading] = useState(false)
  const [titleDialogOpen, setTitleDialogOpen] = useState(false)

  const isAnyFirmSelected = useMemo(() => {
    return Object.keys(rowSelection).some(key => rowSelection[key])
  }, [rowSelection])

  const isDisabled = useMemo(() => {
    return campaignData?.status === 'PREPARING'
  }, [campaignData?.status])

  const hasSelectionChanges = useMemo(() => {
    const currentKeys = Object.keys(rowSelection).filter(key => rowSelection[key])
    const originalKeys = Object.keys(originalRowSelection).filter(key => originalRowSelection[key])

    if (currentKeys.length !== originalKeys.length) {
      return true
    }

    return currentKeys.some(key => !originalRowSelection[key]) || originalKeys.some(key => !rowSelection[key])
  }, [rowSelection, originalRowSelection])

  const filteredFirms = useMemo(() => {
    let filtered = firms

    if (relevantFilter === 'relevant') {
      filtered = filtered.filter(firm => firm.isRelevant === true)
    } else if (relevantFilter === 'not-relevant') {
      filtered = filtered.filter(firm => firm.isRelevant === false)
    }

    if (searchTerm) {
      filtered = filtered.filter(firm =>
        firm.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        firm.emailList?.some(
          email => email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        firm.sector?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return filtered
  }, [firms, relevantFilter, searchTerm])

  const collectSelectionData = useCallback(() => {
    return firms.map((firm) => ({
      id: firm.id,
      selected: Boolean(rowSelection[firm.id])
    }))
  }, [firms, rowSelection])

  const handleSaveSelected = useCallback(async () => {
    setSaveSelectedLoading(true)

    try {
      const campaignFirmList = collectSelectionData()

      const payload = {
        campaignId: queryParams.b2bCampaignId,
        campaignFirmList
      }

      await updateCampaignFirmSelection(payload)

      setOriginalRowSelection({ ...rowSelection })
      setHasUnsavedChanges(false)

      showSuccess(t('mailTemplate.campaignPreparation.saveSelectedSuccess'))
    } catch (error) {
      console.error('Failed to save selected firms:', error)
      showError(t('mailTemplate.campaignPreparation.saveSelectedError'))
    } finally {
      setSaveSelectedLoading(false)
    }
  }, [collectSelectionData, queryParams.b2bCampaignId, rowSelection, showSuccess, showError, t])

  const handleOpenTitleDialog = useCallback(() => {
    if (!isAnyFirmSelected) {
      showError(t('mailTemplate.campaignPreparation.noFirmsSelectedError'))
      return
    }
    setTitleDialogOpen(true)
  }, [isAnyFirmSelected, showError, t])

  const handleCloseTitleDialog = useCallback(() => {
    setTitleDialogOpen(false)
  }, [])

  const onSubmit = useCallback(async (positions) => {
    setNextLoading(true)
    setTitleDialogOpen(false)

    try {
      await approveCampaignFirmSelection(queryParams.b2bCampaignId, positions)

      showSuccess(t('mailTemplate.campaignPreparation.approveSelectionSuccess'))

      if (onCampaignUpdate) {
        await onCampaignUpdate()
      }
    } catch (error) {
      console.error('Failed to submit campaign:', error)
      showError(t('mailTemplate.campaignPreparation.approveSelectionError'))
    } finally {
      setNextLoading(false)
    }
  }, [queryParams.b2bCampaignId, showSuccess, showError, t, onCampaignUpdate])

  const handleRefresh = useCallback(() => {
    if (queryParams?.b2bCampaignId) {
      setFetching(true)

      const minDelayPromise = new Promise(resolve => setTimeout(resolve, 800))
      const apiPromise = getCampaignFirmsAndState({ b2bCampaignId: queryParams.b2bCampaignId })

      Promise.all([apiPromise, minDelayPromise]).then(([res]) => {
        const backendSelection = res.data.campaignFirmList.reduce((acc, firm) => {
          if (firm.selected) {
            acc[firm.id] = true
          }
          return acc
        }, {})

        setFirms(res.data.campaignFirmList)
        setRowSelection(backendSelection)
        setOriginalRowSelection(backendSelection)

        if (res.data.campaignStatus) {
          setCampaignData(prev => ({ ...prev, status: res.data.campaignStatus }))
          setCampaignStatus(queryParams.b2bCampaignId, res.data.campaignStatus)
        }
      }).finally(() => {
        setFetching(false)
      })
    }
  }, [queryParams?.b2bCampaignId, setCampaignStatus])

  const queryParamsError = useCallback((queryParams, b2bAccountIdError, b2bCampaignIdError) => {
    const b2bAccountId = queryParams?.b2bAccountId
    const b2bCampaignId = queryParams?.b2bCampaignId

    if (b2bAccountId && b2bCampaignId) {
      return false
    } else if (b2bCampaignId) {
      return <div>{b2bAccountIdError}</div>
    } else {
      return <div>{b2bCampaignIdError}</div>
    }
  }, [])

  useEffect(() => {
    if (queryParams?.b2bCampaignId) {
      getCampaign(queryParams.b2bCampaignId).then((campaignRes) => {
        setCampaignData(campaignRes.data)
        if (campaignRes.data?.status) {
          setCampaignStatus(queryParams.b2bCampaignId, campaignRes.data.status)
        }
      }).catch((error) => {
        console.error('Failed to fetch campaign data:', error)
      })

      getCampaignFirmsAndState({ b2bCampaignId: queryParams.b2bCampaignId }).then((res) => {
        const backendSelection = res.data.campaignFirmList.reduce((acc, firm) => {
          if (firm.selected) {
            acc[firm.id] = true
          }
          return acc
        }, {})

        setFirms(res.data.campaignFirmList)
        setRowSelection(backendSelection)
        setOriginalRowSelection(backendSelection)

        if (res.data.campaignStatus) {
          setCampaignData(prev => ({ ...prev, status: res.data.campaignStatus }))
          setCampaignStatus(queryParams.b2bCampaignId, res.data.campaignStatus)
        }
      }).finally(() => {
        setFetching(false)
      })
    }
  }, [queryParams.b2bCampaignId, setCampaignStatus])

  useEffect(() => {
    setHasUnsavedChanges(hasSelectionChanges)
  }, [hasSelectionChanges])

  useEffect(() => {
    const shouldPoll = (campaignData?.status === 'PREPARING' || campaignData?.status === 'CONTACTS_SEARCHING') && !fetching

    if (shouldPoll) {
      intervalRef.current = setInterval(() => {
        handleRefresh()
      }, 7500)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [campaignData?.status, fetching, handleRefresh])

  const handleAbort = useCallback(async () => {
    setAbortLoading(true)

    try {
      await abortSelectFirms(queryParams.b2bCampaignId)

      showSuccess(t('mailTemplate.campaignPreparation.abortSuccess'))

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      await handleRefresh()

      if (onCampaignUpdate) {
        await onCampaignUpdate()
      }
    } catch (error) {
      console.error('Failed to abort firm selection:', error)
      showError(t('mailTemplate.campaignPreparation.abortError'))
    } finally {
      setAbortLoading(false)
    }
  }, [queryParams.b2bCampaignId, showSuccess, showError, t, handleRefresh, onCampaignUpdate])

  const handleDownloadExcel = async () => {
    try {
      const relevant =
        relevantFilter === 'all'
          ? null
          : relevantFilter === 'relevant'

      const res = await downloadCampaignFirmsExcel(queryParams.b2bCampaignId, null, relevant)
      const blob = new Blob([res.data], { type: 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `campaign-firms-${queryParams.b2bCampaignId}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      // error
    }
  }

  return (
    queryParamsError(queryParams, t('mailTemplate.wizard.b2bAccountIdError'), t('mailTemplate.wizard.b2bCampaignIdError')) ||
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="p-2" style={{ direction: 'ltr' }}>
        <div className="flex justify-center items-center mb-4">
          <div className="flex flex-col items-center">
            <Psychology sx={{ fontSize: 50 }} color="primary"/>
            <Typography variant="caption" color="primary">
              {t('mailTemplate.campaignPreparation.aiFiltered')}
            </Typography>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
              variant="contained"
              onClick={handleDownloadExcel}
              sx={{ borderRadius: 2 }}
              endIcon={<TableViewIcon/>}
              disabled={!filteredFirms?.length}
            >
              {t('down_xls')}
            </Button>

          <Button
                variant="outlined"
                onClick={handleSaveSelected}
                disabled={saveSelectedLoading || isDisabled || !hasUnsavedChanges}
                startIcon={saveSelectedLoading ? <CircularProgress sx={{ color: 'inherit' }} size={18}/> : null}
                sx={{ borderRadius: 2 }}
              >
                {saveSelectedLoading ? t('mailTemplate.campaignPreparation.savingSelected') : t('mailTemplate.campaignPreparation.saveSelected')}
              </Button>
              <Tooltip
                title={hasUnsavedChanges ? t('mailTemplate.campaignPreparation.saveBeforeStart') : ''}
                arrow
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleOpenTitleDialog}
                    disabled={nextLoading || isDisabled || hasUnsavedChanges}
                    startIcon={nextLoading ? <CircularProgress sx={{ color: 'inherit' }} size={18}/> : null}
                    sx={{ borderRadius: 2 }}
                  >
                    {nextLoading ? t('mailTemplate.campaignPreparation.approvingSelection') : t('mailTemplate.campaignPreparation.approveSelection')}
                  </Button>
                </span>
              </Tooltip>
        </div>
      </div>

      <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {campaignData?.status === 'PREPARING' && (
            <>
              <IconButton
                onClick={handleRefresh}
                disabled={fetching}
                color="primary"
                sx={{ borderRadius: 2 }}
              >
                {fetching ? <CircularProgress size={24}/> : <Refresh/>}
              </IconButton>
              <Button
                variant="outlined"
                color="error"
                onClick={handleAbort}
                disabled={abortLoading}
                startIcon={abortLoading ? <CircularProgress sx={{ color: 'inherit' }} size={18}/> : null}
                sx={{ borderRadius: 2 }}
              >
                {abortLoading ? t('mailTemplate.campaignPreparation.aborting') : t('mailTemplate.campaignPreparation.abort')}
              </Button>
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <ToggleButtonGroup
              value={relevantFilter}
              exclusive
              onChange={(_, newValue) => newValue &&
                setRelevantFilter(newValue)}
              size="large"
              sx={{
                '& .MuiToggleButton-root': {
                  py: 1,
                  px: 2.5,
                  fontSize: '14px',
                  minWidth: 'auto',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark'
                    }
                  }
                }
              }}
            >
              <ToggleButton value="all">
                {t('mailTemplate.campaignReview.all')}
              </ToggleButton>
              <ToggleButton value="relevant">
                {t('mailTemplate.campaignReview.relevant')}
              </ToggleButton>
              <ToggleButton value="not-relevant">
                {t('mailTemplate.campaignReview.notRelevant')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box> */}

          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('mailTemplate.campaigns.search')}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search/>
                </InputAdornment>
              )
            }}
          />
          <FormControl sx={{ minWidth: 80 }}>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              size="small"
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {showProgressBar && (
        <CampaignProgressBar
          campaign={campaignData || campaign}
          firms={firms}
        />
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginTop: '10px' }}>
        <CampaignPreparationTable
          data={filteredFirms}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          fetching={fetching}
          fuzzyFilter={fuzzyFilter}
          globalFilter={searchTerm}
          setGlobalFilter={setSearchTerm}
          pageSize={pageSize}
          disableInteraction={isDisabled}
          hideCheckboxes={false}
        />
      </Box>

      <TitleFilterDialog
        open={titleDialogOpen}
        onConfirm={onSubmit}
        onCancel={handleCloseTitleDialog}
        loading={nextLoading}
      />
    </Box>
  )
}

export default CampaignPreparation
